import simpleRestProvider from 'ra-data-simple-rest';
import { config } from '../../config/config';
import type { DataProvider, GetListParams, GetListResult } from 'react-admin';

const httpClient = async (
  url: string,
  options: RequestInit = {}
): Promise<{ status: number; headers: Headers; body: string; json: unknown }> => {
  const headers = new Headers(options.headers || {});

  if (options.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }

  const token = localStorage.getItem('token');
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(url, { ...options, headers });

  if (response.status === 401 || response.status === 403) {
    const error = new Error('Authentication expired');
    (error as Error & { status: number }).status = response.status;
    throw error;
  }

  const text = await response.text();

  let json = null;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      if (response.status >= 400) {
        throw new Error(text);
      }
      json = null;
    }
  }

  return {
    status: response.status,
    headers: response.headers,
    body: text,
    json,
  };
};

const baseProvider = simpleRestProvider(`${config.apiUrl}/api`, httpClient);

// Custom data provider that converts React Admin 5.x format to backend format
const dataProviderWithAuth: DataProvider = {
  ...baseProvider,
  getList: async <RecordType extends { id: number | string }>(
    resource: string,
    params: GetListParams
  ): Promise<GetListResult<RecordType>> => {
    const { page, perPage } = params.pagination || { page: 1, perPage: 10 };
    const { field, order } = params.sort || { field: 'id', order: 'ASC' };

    // Convert React Admin 5.x format to backend format
    const query = {
      _page: page,
      _perPage: perPage,
      _sort: field,
      _order: order,
      ...params.filter,
    };

    const url = `${config.apiUrl}/api/${resource}?${new URLSearchParams(
      Object.entries(query).reduce(
        (acc, [key, value]) => {
          if (value !== undefined && value !== null) {
            acc[key] = String(value);
          }
          return acc;
        },
        {} as Record<string, string>
      )
    ).toString()}`;

    const response = await httpClient(url);

    if (!response.headers.has('x-total-count')) {
      throw new Error(
        'The X-Total-Count header is missing in the HTTP Response. The simple REST data provider expects responses for lists of resources to contain this header with the total number of results to build the pagination. If you are using CORS, did you declare X-Total-Count in the Access-Control-Expose-Headers header?'
      );
    }

    return {
      data: response.json as RecordType[],
      total: parseInt(response.headers.get('x-total-count') || '0', 10),
    };
  },
};

export default dataProviderWithAuth;
